import { Breadcrumb } from "antd";
import { Edit, Eye, Trash } from "lucide-react";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getPodcastsClient, setPodcast } from "../../../../slices/podcastSlice";
import PodcastDelete from "../../../../components/modales/podcast/PodcastDelete";
import { setClient } from "../../../../slices/clientesSlice";

export default function PodcastsCliente() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  //* Estados modales
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const { client } = useSelector(state => state.clientes);
  const { podcastsClient } = useSelector(state => state.podcast);

  //* Accionadores modales
  const handleModalView = (podcast) => {
    dispatch(setPodcast(podcast));
    setIsDeleteModalOpen(true);
  };

  const handleModalDelete = (podcast) => {
    dispatch(setPodcast(podcast));
    setIsDeleteModalOpen(true);
  };

  const colors = [
    "bg-blue-600",
    "bg-purple-600",
    "bg-red-600",
    "bg-yellow-500",
    "bg-green-500",
  ];

  const getInitials = (name) => {
    return name
      ?.split(" ")
      .map((word) => word.charAt(0).toUpperCase())
      .join("");
  };

  const handleNavigation = (podcast) => {
    dispatch(setPodcast(podcast));
    navigate("/admin/podcastCliente");
  };

  useEffect(() => {
    if (client?._id) {
      dispatch(getPodcastsClient(client._id));
    }
  }, [dispatch, client]);

  return (
    <div className="w-full min-h-screen flex flex-col font-poppina bg-background">
      <PodcastDelete
        isDeleteModal={isDeleteModalOpen}
        setIsDeleteModal={setIsDeleteModalOpen}
      />

      <div className="mt-3 ml-5 flex items-center">
        <p
          onClick={() => navigate("/admin/clientes")}
          className="text-subtitle cursor-pointer hover:text-text "
        >
          Clientes
        </p>
        <p className="mx-3 text-subtitle text-xs">/</p>
        <p className="text-text">{client?.name}</p>
      </div>

      <div className="my-10">
        <h1 className="text-5xl text-center text-text mb-10 font-semibold">
          Podcasts de {client?.name}
        </h1>
      </div>

      <div className="flex flex-wrap justify-center gap-12">
        {podcastsClient?.length > 0 ? (
          podcastsClient.map((podcast, index) => (
            <div
              key={podcast.id}
              className="card-container hover:scale-[1.02] ease-in-out bg-background rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 h-[520px] w-[320px] overflow-hidden flex flex-col justify-between"
            >
              <div
                className={`relative min-h-40 max-h-40 overflow-hidden text-nowrap flex justify-center items-center ${
                  colors[index % colors.length]
                }`}
              >
                <h1 className="text-white text-5xl">
                  {getInitials(podcast?.name)}
                </h1>
                {podcast?.newIdea && (
                  <span className="absolute top-4 right-4 bg-black text-white text-xs font-medium px-3 py-1 rounded-full shadow-sm">
                    Nueva Idea
                  </span>
                )}
              </div>

              <div className="p-6 flex flex-col flex-grow">
                <div className="mb-4">
                  <h2 className="text-2xl font-bold text-text mb-2">
                    {podcast?.name}
                  </h2>
                  <p className="text-sm text-subtitle mb-4">
                    {podcast?.description}
                  </p>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center text-sm text-gray-700">
                    <svg
                      className="w-4 h-4 mr-2 text-gray-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <span>
                      <span className="font-medium">Plan Mensual:</span>{" "}
                      {podcast?.monthlyPlan === true ? "Activo" : "Inactivo"}
                    </span>
                  </div>

                  <div className="flex items-center text-sm text-gray-700">
                    <svg
                      className="w-4 h-4 mr-2 text-gray-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    <span>
                      <span className="font-medium">Plan Activo:</span>{" "}
                      {podcast?.planActive ? "Sí" : "No"}
                    </span>
                  </div>

                  <div className="flex items-center text-sm text-gray-700">
                    <svg
                      className="w-4 h-4 mr-2 text-gray-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0-4H3m15 0h3m-3-7a7 7 0 00-7-7m0 0a7 7 0 00-7 7m7-7v4m0-4h14m-14 0H3"
                      />
                    </svg>
                    <span>
                      <span className="font-medium">Solo Audio:</span>{" "}
                      {podcast?.onlyAudio ? "Sí" : "No"}
                    </span>
                  </div>
                </div>
              </div>

              <div className="border-t border-border py-5">
                <div className="flex justify-center items-center gap-5">
                  <button onClick={() => handleNavigation(podcast)} className="p-2 bg-purple-500 text-purple-100 rounded hover:scale-[1.03]">
                    <Eye />
                  </button>
                  <button className="p-2 bg-blue-500 text-blue-100 rounded hover:scale-[1.03]">
                    <Edit />
                  </button>
                  <button onClick={() => handleModalDelete(podcast)} className="p-2 bg-red-500 text-red-100 rounded hover:scale-[1.03]">
                    <Trash />
                  </button>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="w-full text-center">
            <h1 className="text-4xl font-semibold text-gray-800">
              No hay podcasts...
            </h1>
          </div>
        )}
      </div>
    </div>
  );
}